import { lazy, Suspense } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import type { ReactElement } from "react";
import { useAuth } from "./app/auth";
import { useI18n } from "./i18n";
import { AppShell } from "./components/AppShell";
import { EmptyState, Spinner } from "./components/ui";

// Lazy load feature pages for code splitting
const FeedPage = lazy(() => import("./features/feed/FeedPage").then(m => ({ default: m.FeedPage })));
const LoginPage = lazy(() => import("./features/auth/LoginPage").then(m => ({ default: m.LoginPage })));
const OAuthCallbackPage = lazy(() => import("./features/auth/OAuthCallbackPage").then(m => ({ default: m.OAuthCallbackPage })));
const UploadPage = lazy(() => import("./features/upload/UploadPage").then(m => ({ default: m.UploadPage })));
const MyVideosPage = lazy(() => import("./features/videos/MyVideosPage").then(m => ({ default: m.MyVideosPage })));
const VideoDetailPage = lazy(() => import("./features/videos/VideoDetailPage").then(m => ({ default: m.VideoDetailPage })));
const ProfilePage = lazy(() => import("./features/profile/ProfilePage").then(m => ({ default: m.ProfilePage })));
const AdminPage = lazy(() => import("./features/admin/AdminPage").then(m => ({ default: m.AdminPage })));

function BootScreen() {
  return (
    <div className="grid min-h-dvh place-items-center">
      <Spinner className="size-7" />
    </div>
  );
}

function LoadingFallback() {
  return (
    <div className="grid min-h-[50vh] place-items-center">
      <Spinner className="size-7" />
    </div>
  );
}

function RequireAuth({ children }: { children: ReactElement }) {
  const { user, booting } = useAuth();
  const location = useLocation();
  if (booting) return <BootScreen />;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  return children;
}

function RequireAdmin({ children }: { children: ReactElement }) {
  const { user, booting } = useAuth();
  const { t } = useI18n();
  const location = useLocation();
  if (booting) return <BootScreen />;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  if (!user.is_admin) return <EmptyState title={t("admin.forbidden")} />;
  return children;
}

function NotFoundPage() {
  const { t } = useI18n();
  return <EmptyState title={t("common.notFound")} hint={t("common.notFoundHint")} />;
}

export function App() {
  return (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<Navigate to="/login" replace />} />
        <Route path="/auth/callback" element={<OAuthCallbackPage />} />
        <Route element={<AppShell />}>
          <Route index element={<FeedPage />} />
          <Route path="v/:videoId" element={<VideoDetailPage />} />
          <Route path="u/:username" element={<ProfilePage />} />
          <Route
            path="upload"
            element={
              <RequireAuth>
                <UploadPage />
              </RequireAuth>
            }
          />
          <Route
            path="mine"
            element={
              <RequireAuth>
                <MyVideosPage />
              </RequireAuth>
            }
          />
          <Route
            path="admin"
            element={
              <RequireAdmin>
                <AdminPage />
              </RequireAdmin>
            }
          />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </Suspense>
  );
}
